import React, { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import PageLoader from '../common/PageLoader';
import {
  Avatar, Drawer, Dropdown, Grid, Layout, Menu, Space, theme,
} from 'antd';
import type { MenuProps } from 'antd';

import {
  AppstoreOutlined, AuditOutlined, BankOutlined, BellOutlined, BookOutlined, BuildOutlined,
  CloudDownloadOutlined,
  DashboardOutlined, FileTextOutlined, GlobalOutlined, HomeOutlined,
  KeyOutlined, LogoutOutlined, MenuOutlined,
  PlusCircleOutlined, SearchOutlined, SettingOutlined, SolutionOutlined, TagsOutlined,
  TeamOutlined, UserOutlined,
} from '@ant-design/icons';
import AdminCompanySelector from '../common/AdminCompanySelector';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchMyRights, logout } from '../../store/slices/authSlice';
import { clearAdminContext } from '../../store/slices/adminContextSlice';
import { usePermission } from '../../hooks/usePermission';
import { UserRole } from '../../types';
import WorkWavLogo from '../common/WorkWavLogo';
import CountrySelector from '../common/CountrySelector';
import CookieBanner from '../common/CookieBanner';
import NotificationsDropdown from '../common/NotificationsDropdown';
import PWAInstallButton from '../common/PWAInstallButton';
import { resolveFileUrl } from '../../utils/fileUrl';

const { Header, Content, Footer } = Layout;
const { useBreakpoint } = Grid;

const ROLE_LABELS: Record<string, string> = {
  [UserRole.SuperAdmin]:    'Super Admin',
  [UserRole.Admin]:         'Admin',
  [UserRole.EmployerAdmin]: 'Employer Admin',
  [UserRole.JobSeeker]:     'Job Seeker',
  [UserRole.Student]:       'Student',
  [UserRole.Employer]:      'Employer',
  SuperAdmin:    'Super Admin',
  Admin:         'Admin',
  EmployerAdmin: 'Employer Admin',
  JobSeeker:     'Job Seeker',
  Student:       'Student',
  Employer:      'Employer',
};

// Keys that match leaf routes AND their sub-paths
const SELECTED_KEY_ORDER = [
  '/jobs/create',
  '/jobs',
  '/applications/received',
  '/applications',
  '/applicants',
  '/admin/notifications',
  '/admin/external-jobs/job-data-lake',
  '/admin/audit',
  '/admin/companies',
  '/admin/jobs',
  '/admin/users',
  '/admin/rights',
  '/admin/settings',
  '/admin/locations',
  '/admin/interview',
  '/admin/quiz',
  '/admin/industries',
  '/interview-questions',
  '/community-interview',
  '/quiz/history',
  '/quiz',
  '/applications/company',
  '/super-dashboard',
  '/my-company',
  '/company-dashboard',
  '/my-personal-jobs',
  '/my-jobs',
  '/companies',
  '/dashboard',
  '/ats',
  '/profile',
  '/change-password',
  '/about',
];

const AppLayout: React.FC = () => {
  const location = useLocation();
  const navigate  = useNavigate();
  const dispatch  = useAppDispatch();
  const { user, rights }  = useAppSelector((s) => s.auth);
  const { selectedCompanyId } = useAppSelector((s) => s.adminContext);
  const { isAdmin, isSuperAdmin, isAnyEmployer, isEmployerAdmin, isJobSeeker, isStudent } = usePermission();
  const isPureAdmin = isAdmin() && !isSuperAdmin();
  const { token } = theme.useToken();
  const screens   = useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (user && rights.length === 0) {
      dispatch(fetchMyRights());
    }
  }, [user, rights.length, dispatch]);

  const handleLogout = () => {
    dispatch(clearAdminContext());
    dispatch(logout());
    navigate('/');
  };

  // ── Reusable nav item definitions ─────────────────────────────────────────

  const NAV = {
    // ── Shared direct links ──────────────────────────────────────────────────
    home:          { key: '/',                icon: <HomeOutlined />,       label: 'Home'        } as const,
    dashboard:     { key: '/dashboard',       icon: <DashboardOutlined />,  label: 'Dashboard'   } as const,
    superDash:     { key: '/super-dashboard', icon: <DashboardOutlined />,  label: 'Dashboard'   } as const,
    findJobs:      { key: '/jobs',            icon: <SearchOutlined />,     label: 'Find Jobs'   } as const,
    postJob:       { key: '/jobs/create',     icon: <PlusCircleOutlined />, label: 'Post a Job'  } as const,
    applicants:    { key: '/applicants',      icon: <SolutionOutlined />,   label: 'Find Applicants' } as const,
    findTalent:    { key: '/applicants',      icon: <TeamOutlined />,       label: 'Find Talent' } as const,
    companies:     { key: '/companies',       icon: <BuildOutlined />,      label: 'Companies'   } as const,

    // ── SuperAdmin — 3 focused submenus ─────────────────────────────────────
    saManage: {
      key: 'sa-manage',
      icon: <AppstoreOutlined />,
      label: 'Manage',
      children: [
        { key: '/admin/companies', icon: <BankOutlined />,     label: 'Companies'     },
        { key: '/admin/jobs',      icon: <FileTextOutlined />, label: 'All Jobs'      },
        { key: '/admin/users',     icon: <TeamOutlined />,   label: 'All Users'     },
        { key: '/admin/rights',    icon: <KeyOutlined />,    label: 'Rights & Roles'},
      ],
    },
    saContent: {
      key: 'sa-content',
      icon: <BookOutlined />,
      label: 'Content',
      children: [
        { key: '/admin/quiz',       icon: <BookOutlined />,                           label: 'Quiz Bank'      },
        { key: '/admin/interview',  icon: <span style={{ fontSize: 14 }}>🎙️</span>, label: 'Interview Bank' },
        { key: '/admin/industries', icon: <TagsOutlined />,                           label: 'Industries'     },
        { key: '/admin/locations',  icon: <GlobalOutlined />,                         label: 'Locations'      },
      ],
    },
    saSystem: {
      key: 'sa-system',
      icon: <SettingOutlined />,
      label: 'System',
      children: [
        { key: '/admin/settings',      icon: <SettingOutlined />, label: 'Settings'       },
        {
          key: 'sa-external-jobs',
          icon: <CloudDownloadOutlined />,
          label: 'External Jobs',
          children: [
            { key: '/admin/external-jobs/job-data-lake', label: 'Job Data Lake' },
          ],
        },
        { key: '/admin/audit',         icon: <AuditOutlined />,   label: 'Audit History'  },
        { key: '/admin/notifications', icon: <BellOutlined />,    label: 'Notifications'  },
      ],
    },

    // ── Admin — Company submenu (shown only when a company is selected) ──────
    adminCompany: {
      key: 'admin-company',
      icon: <BankOutlined />,
      label: 'Company',
      children: [
        { key: '/company-dashboard',    icon: <DashboardOutlined />, label: 'Company Dashboard'    },
        { key: '/my-jobs',              icon: <AppstoreOutlined />,  label: 'Company Jobs'         },
        { key: '/my-company',           icon: <BankOutlined />,      label: 'Company Profile'      },
        { key: '/applications/company', icon: <FileTextOutlined />,  label: 'Company Applications' },
      ],
    },

    // ── EmployerAdmin — My Jobs + Company submenus ───────────────────────────
    eaMyJobs: {
      key: 'ea-my-jobs',
      icon: <SolutionOutlined />,
      label: 'My Jobs',
      children: [
        { key: '/dashboard',             icon: <DashboardOutlined />,  label: 'Dashboard'             },
        { key: '/my-personal-jobs',      icon: <SolutionOutlined />,   label: 'My Posted Jobs'        },
        { key: '/applications/received', icon: <FileTextOutlined />,   label: 'Applications Received' },
      ],
    },
    eaCompany: {
      key: 'ea-company',
      icon: <BankOutlined />,
      label: 'Company',
      children: [
        { key: '/company-dashboard',     icon: <DashboardOutlined />,  label: 'Company Dashboard' },
        { key: '/my-jobs',               icon: <AppstoreOutlined />,   label: 'Company Jobs'      },
        { key: '/my-company',            icon: <BankOutlined />,       label: 'Company Profile'   },
        { key: '/applications/company',  icon: <FileTextOutlined />,   label: 'All Applications'  },
      ],
    },

    // ── Employer — My Jobs submenu ───────────────────────────────────────────
    empMyJobs: {
      key: 'emp-my-jobs',
      icon: <SolutionOutlined />,
      label: 'My Jobs',
      children: [
        { key: '/my-jobs',               icon: <AppstoreOutlined />,   label: 'Company Posted Jobs'   },
        { key: '/applications/received', icon: <FileTextOutlined />,   label: 'Applications Received' },
      ],
    },

    // ── Applicant unified submenus ───────────────────────────────────────────
    myCareer: {
      key: 'my-career',
      icon: <SolutionOutlined />,
      label: 'My Career',
      children: [
        { key: '/dashboard',    icon: <DashboardOutlined />, label: 'Dashboard'       },
        { key: '/applications', icon: <FileTextOutlined />,  label: 'My Applications' },
      ],
    },
    skillBuilder: {
      key: 'skill-builder',
      icon: <BookOutlined />,
      label: 'Skill Builder',
      children: [
        { key: '/interview-questions', icon: <span style={{ fontSize: 14 }}>🎙️</span>, label: 'Interview Prep'    },
        { key: '/community-interview', icon: <span style={{ fontSize: 14 }}>🏢</span>, label: 'Company Q&A'       },
        { key: '/quiz',                icon: <span style={{ fontSize: 14 }}>📝</span>, label: 'Take Quiz'         },
        { key: '/quiz/history',        icon: <span style={{ fontSize: 14 }}>📊</span>, label: 'Quiz History'      },
        { key: '/ats',                 icon: <span style={{ fontSize: 14 }}>🎯</span>, label: 'ATS Resume Check'  },
      ],
    },
  };

  // ── Role-ordered nav ───────────────────────────────────────────────────────
  // Each role sees its most-used actions first; secondary items trail.

  let navItems: MenuProps['items'];

  if (isSuperAdmin()) {
    // Home · Dashboard · Manage ▾ · Content ▾ · System ▾
    navItems = [NAV.home, NAV.superDash, NAV.saManage, NAV.saContent, NAV.saSystem];
  } else if (isPureAdmin) {
    // Home · Dashboard · Post a Job · Find Talent · Company ▾ (only when company selected)
    navItems = [
      NAV.home,
      NAV.dashboard,
      NAV.postJob,
      { key: 'admin-external-jobs', icon: <CloudDownloadOutlined />, label: 'External Jobs', children: [
        { key: '/admin/external-jobs/job-data-lake', label: 'Job Data Lake' },
      ] },
      NAV.findTalent,
      ...(selectedCompanyId ? [NAV.adminCompany] : []),
    ];
  } else if (isEmployerAdmin()) {
    // Home · Post a Job · My Jobs ▾ · Company ▾ · Find Talent
    navItems = [NAV.home, NAV.postJob, NAV.eaMyJobs, NAV.eaCompany, NAV.findTalent];
  } else if (isAnyEmployer()) {
    // Home · Dashboard · Post a Job · My Jobs ▾ · Find Talent
    navItems = [NAV.home, NAV.dashboard, NAV.postJob, NAV.empMyJobs, NAV.findTalent];
  } else if (isStudent() || isJobSeeker()) {
    // Home · Find Jobs · My Career ▾ · Skill Builder ▾ · Companies
    navItems = [
      NAV.home,
      NAV.findJobs,
      NAV.myCareer,
      NAV.skillBuilder,
      NAV.companies,
    ];
  } else {
    navItems = [NAV.home, NAV.dashboard, NAV.findJobs, NAV.companies];
  }

  // ── Selected key ──────────────────────────────────────────────────────────
  const selectedKey =
    location.pathname === '/'
      ? '/'
      : SELECTED_KEY_ORDER.find(
          (k) => location.pathname === k || location.pathname.startsWith(k + '/'),
        ) ?? '/dashboard';

  // ── User dropdown ─────────────────────────────────────────────────────────
  const dropdownItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'My Profile',
      onClick: () => navigate('/profile'),
    },
    // Admin/SuperAdmin: all management is accessible from the Admin / Super Admin nav submenus
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      onClick: handleLogout,
    },
  ];

  const roleLabel = user ? (ROLE_LABELS[user.role] ?? 'User') : 'User';
  const initials  = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'U';

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          background: token.colorBgContainer,
          padding: screens.md ? '0 32px' : '0 16px',
          display: 'flex',
          alignItems: 'center',
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          height: 56,
          gap: 0,
        }}
      >
        {/* Logo */}
        <div
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer', marginRight: 24, flexShrink: 0 }}
        >
          <WorkWavLogo size={40} fontSize={18} />
        </div>

        {/* Nav menu — full horizontal on md+, hamburger on mobile */}
        {screens.md ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0, gap: 4 }}>
            <Menu
              mode="horizontal"
              selectedKeys={[selectedKey]}
              items={navItems}
              onClick={({ key }) => { if (key.startsWith('/')) navigate(key); }}
              style={{ flex: 1, border: 'none', minWidth: 0, background: 'transparent' }}
              overflowedIndicator={<span style={{ fontWeight: 600 }}>More ▾</span>}
            />

          </div>
        ) : (
          <div style={{ flex: 1 }} />
        )}

        {/* Right: company selector (Admin) + install + country + bell + avatar + hamburger */}
        <Space size={8} style={{ marginLeft: 'auto', flexShrink: 0 }}>
          {!screens.md && (
            <MenuOutlined
              style={{ fontSize: 20, cursor: 'pointer', color: token.colorText }}
              onClick={() => setDrawerOpen(true)}
            />
          )}
          <PWAInstallButton compact={!screens.md} />

          {/* Admin: company selector only in right-side area (Employer Admin menu is in nav row above) */}
          {screens.md && isPureAdmin && (
            <AdminCompanySelector width={260} />
          )}

          {screens.sm && <CountrySelector transparent={false} size="small" />}

          <NotificationsDropdown />

          <Dropdown menu={{ items: dropdownItems }} placement="bottomRight" arrow trigger={['click']}>
            <Space style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: 8 }}>
              {user?.avatarUrl
                ? <Avatar src={resolveFileUrl(user.avatarUrl)} size={34} />
                : (
                  <Avatar size={34} style={{ background: '#56B0FF', fontWeight: 700, fontSize: 13 }}>
                    {initials}
                  </Avatar>
                )}
              {screens.md && (
                <div style={{ lineHeight: 1.35 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div style={{ fontSize: 11, color: '#888' }}>{roleLabel}</div>
                </div>
              )}
            </Space>
          </Dropdown>
        </Space>
      </Header>

      <Content
        style={{
          padding: screens.md ? '16px 32px' : '12px 16px',
          background: '#f5f7fa',
          minHeight: 'calc(100vh - 56px - 54px)',
        }}
      >
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </Content>

      <CookieBanner />

      <Footer
        style={{
          textAlign: 'center',
          background: token.colorBgContainer,
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          padding: '14px 32px',
          color: '#888',
          fontSize: 13,
        }}
      >
        WorkWav &copy; {new Date().getFullYear()} — Ride Your Career Wave
      </Footer>

      {/* Mobile navigation drawer */}
      <Drawer
        title={<WorkWavLogo size={32} fontSize={16} />}
        placement="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={280}
        styles={{ body: { padding: 0 } }}
      >
        {/* Admin: company selector in drawer so they can switch company context */}
        {isPureAdmin && (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Manage Company
            </div>
            <AdminCompanySelector compact />
          </div>
        )}

        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={navItems}
          onClick={({ key }) => {
            if (key.startsWith('/')) { navigate(key); setDrawerOpen(false); }
          }}
          style={{ border: 'none', fontSize: 15 }}
        />

        <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0' }} />
        <Menu
          mode="inline"
          selectable={false}
          items={dropdownItems.filter(i => i && (i as { key?: string }).key !== 'settings')}
          style={{ border: 'none', fontSize: 15 }}
          onClick={({ key }) => {
            if (key === 'logout') { handleLogout(); setDrawerOpen(false); }
            else if (key === 'profile') { navigate('/profile'); setDrawerOpen(false); }
            else if (key === 'my-personal-jobs') { navigate('/my-personal-jobs'); setDrawerOpen(false); }
            else if (key === 'my-jobs') { navigate('/my-jobs'); setDrawerOpen(false); }
            else if (key === 'company-dashboard') { navigate('/company-dashboard'); setDrawerOpen(false); }
            else if (key === 'my-company') { navigate('/my-company'); setDrawerOpen(false); }
            else if (key === 'change-password') { navigate('/change-password'); setDrawerOpen(false); }
          }}
        />
      </Drawer>
    </Layout>
  );
};

export default AppLayout;
